import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CustomCursor from "@/components/CustomCursor";

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Products = lazy(() => import("./pages/Products"));
const Services = lazy(() => import("./pages/Services"));
const Network = lazy(() => import("./pages/Network"));
const Clientele = lazy(() => import("./pages/OurClients"));
const Factories = lazy(() => import("./pages/Factories"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ElevenLabsVoiceAgent = lazy(() => import("@/components/ElevenLabsVoiceAgent"));
const BackToTop = lazy(() => import("@/components/BackToTop"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.replace("#", ""));
      let cancelled = false;
      let attempts = 0;
      // The target page is lazy-loaded, so poll until the section exists.
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts++ < 40) setTimeout(tryScroll, 100);
        else window.scrollTo(0, 0);
      };
      tryScroll();
      return () => {
        cancelled = true;
      };
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};


const DESKTOP_BREAKPOINT = 1024;

const FooterRevealLayout = () => {
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [mountVoiceAgent, setMountVoiceAgent] = useState(false);

  // Defer loading the ElevenLabs voice-agent chunk until the browser is idle
  // so it doesn't compete with the initial render.
  useEffect(() => {
    const idle = (cb: () => void) =>
      "requestIdleCallback" in window
        ? (window as any).requestIdleCallback(cb, { timeout: 4000 })
        : setTimeout(cb, 2500);
    const handle = idle(() => setMountVoiceAgent(true));
    return () => {
      if ("cancelIdleCallback" in window) (window as any).cancelIdleCallback(handle);
      else clearTimeout(handle as unknown as number);
    };
  }, []);

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    checkDesktop();
    const onResize = () => checkDesktop();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    // Footer height is only applied to layout on desktop. Skip all measurement
    // work on mobile to avoid unnecessary reflows during initial paint.
    if (!isDesktop) return;

    let raf = 0;
    let lastHeight = -1;
    const updateHeight = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const next = footerRef.current ? footerRef.current.offsetHeight : 0;
        if (next !== lastHeight) {
          lastHeight = next;
          setFooterHeight(next);
        }
      });
    };

    // Defer initial measurement until the browser is idle so it doesn't
    // force a layout during the critical first-paint path.
    const idle = (cb: () => void) =>
      "requestIdleCallback" in window
        ? (window as any).requestIdleCallback(cb, { timeout: 1000 })
        : setTimeout(cb, 200);
    const idleHandle = idle(updateHeight);

    const observer = new ResizeObserver(updateHeight);
    if (footerRef.current) observer.observe(footerRef.current);

    window.addEventListener("resize", updateHeight);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if ("cancelIdleCallback" in window) (window as any).cancelIdleCallback(idleHandle);
      else clearTimeout(idleHandle as unknown as number);
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [isDesktop]);

  return (
    <>
      <ScrollToTop />
      <CustomCursor />
      <Navbar />
      <div
        className="relative z-10 bg-background"
        style={isDesktop ? {
          marginBottom: footerHeight,
          boxShadow: "0 20px 60px -10px rgba(0,0,0,0.3)",
        } : undefined}
      >
        <main>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/products" element={<Products />} />
              <Route path="/services" element={<Services />} />
              <Route path="/network" element={<Network />} />
              <Route path="/clientele" element={<Clientele />} />
              <Route path="/factories" element={<Factories />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      {isDesktop ? (
        <div ref={footerRef} className="fixed bottom-0 left-0 right-0 z-0">
          <Footer />
        </div>
      ) : (
        <Footer />
      )}
      {mountVoiceAgent && (
        <Suspense fallback={null}>
          <ElevenLabsVoiceAgent />
        </Suspense>
      )}
      {mountVoiceAgent && (
        <Suspense fallback={null}>
          <BackToTop />
        </Suspense>
      )}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation} strict>
      <Toaster />
      <BrowserRouter>
        <FooterRevealLayout />
      </BrowserRouter>
    </LazyMotion>
  </QueryClientProvider>
);

export default App;
